import { useTranslation } from 'react-i18next';
import type {
  Conversation,
  ConversationMessage,
} from '~/pages/messages/Messages';
import { NotificationSentCard } from './cards/NotificationSentCard';
import { OrderCancelledCard } from './cards/OrderCancelledCard';
import { OrderConfirmedCard } from './cards/OrderConfirmedCard';
import { OrderNotificationCard } from './cards/OrderNotificationCard';
import { OrderReminderCard } from './cards/OrderReminderCard';
import { PackingListNotificationCard } from './cards/PackingListNotificationCard';
import { PriceDeclinedCard } from './cards/PriceDeclinedCard';
import { TextMessageCard } from './cards/TextMessageCard';

interface MessageThreadProps {
  conversation: Conversation;
  messages: ConversationMessage[];
  isLoading?: boolean;
}

function MessageCard({ msg }: Readonly<{ msg: ConversationMessage }>) {
  switch (msg.type) {
    case 'order_notification':
      return <OrderNotificationCard msg={msg} />;
    case 'text':
      return <TextMessageCard msg={msg} />;
    case 'order_cancelled':
      return <OrderCancelledCard msg={msg} />;
    case 'order_confirmed':
      return <OrderConfirmedCard msg={msg} />;
    case 'order_reminder':
      return <OrderReminderCard msg={msg} />;
    case 'price_declined':
      return <PriceDeclinedCard msg={msg} />;
    case 'packing_list_notification':
      return <PackingListNotificationCard msg={msg} />;
    case 'notification_sent':
      return <NotificationSentCard msg={msg} />;
  }
}

export default function MessageThread({
  conversation,
  messages,
  isLoading = false,
}: Readonly<MessageThreadProps>) {
  const { t } = useTranslation('messages');
  return (
    <div className="flex flex-col flex-1 min-w-0">
      <div className="flex items-center gap-3 px-6 py-3 border-b border-neut-border">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-neut-0 text-sm font-semibold shrink-0 ${conversation.avatarBg}`}
        >
          {conversation.initials}
        </div>
        <div>
          <p className="text-sm font-semibold text-neut-prim">
            {conversation.name}
          </p>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-prim inline-block" />
            <span className="text-xs text-neut-500">
              {conversation.organizationName}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-neut-sec text-sm">
            {t('messages.loading')}
          </div>
        ) : (
          messages.map((msg) => <MessageCard key={msg.id} msg={msg} />)
        )}
      </div>
    </div>
  );
}
